/**
 * Employee self-service "My Pay" resolver hook (C7 PR B, #21938).
 *
 * A self-fetching hook, NOT a `dev_*.ts`/`useDevBackendData` boot-time seed
 * — see `wagepro_store_my_pay.ts`'s module docblock for why: this is real
 * production OAuth-gated auth work, and the step-up retry loop needs a
 * genuine re-fetch a boot-time seed can't express.
 *
 * Uses the COOKIE client (`getTrpcClient()`, `lib/trpc.ts`). This changed:
 * it was previously `getBearerTrpcClient()`, on the reasoning that this
 * surface is the Personal-Workspace employee ESS plane (ADR-030) and must
 * stay OAuth-Bearer-only per `docs/architecture-boundaries.md`'s
 * OAuth-scoped-grant invariant.
 *
 * That invariant governs the SEPARATE personal-workspace Worker — a distinct
 * tenancy plane that must never hold a business session cookie. `wagepro-web`
 * is not that Worker: it is a first-party app whose entire session is now a
 * cookie (`resolveAuthBoot`, `lib/auth/auth_context.tsx`), and the API
 * explicitly admits first-party cookie callers on exactly these procedures —
 * `personalPayroll.listEmployerLinks`/`listPayslips` document that their
 * `requireNonNullAudience`/`requireOAuthScope` guards no-op when
 * `ctx.oauthScopes` is null, and `apps/tax-web`'s pay screens have always
 * called them that way. The bearer client is `credentials`-less by design
 * (`lib/trpc.ts`), so against a cookie session it sent NO credential at all
 * and every My Pay fetch failed unauthenticated — the employee got a booted
 * shell with no data.
 *
 * Two calls: `personalPayroll.listEmployerLinks` (the employer picker
 * source), then `personalPayroll.listPayslips` for whichever entity is
 * selected.
 * `getPayslip` is never called — see `wagepro_store_my_pay.ts`'s module
 * docblock.
 *
 * There is NO mock fallback here (unlike C2-C6's admin-screen resolvers,
 * which keep a mock-backed screen usable when one resolver degrades) — a
 * mock payslip shown to an employee as their own real pay would be
 * fabricated financial data, not a harmless placeholder. Every failure
 * surfaces as a real `MyPayState` (`step_up_required`/`error`) instead;
 * `MOCK_MY_PAY` lives only as `wagepro_store_my_pay.test.ts`'s own adapter
 * fixture baseline (not in `mock_data.ts` — nothing at runtime reads it).
 *
 * There is deliberately no `acceptEssInvite` call anywhere in this hook —
 * `personalPayroll.acceptEssInvite` is intentionally absent from the OAuth
 * allowlist (`apps/api/src/trpc/oauth_allowlist.ts:398`): accepting an
 * employer link is the consent action that CREATES the relationship the
 * IDOR guard on `listPayslips`/`getPayslip` depends on, so letting an OAuth
 * token self-grant that would be a privilege-escalation surface, not a
 * missing convenience. The `no_employer_link` state's copy explains this in
 * plain language (the invite is accepted via the employer's email link,
 * over on `apps/tax-web`/`apps/ledger-web`) without linking there — no
 * `resolveTaxWebUrl` helper exists, and inventing one is out of scope here.
 */
import * as React from 'react';
import { getTrpcClient } from '../lib/trpc.js';
import { isAal2Required } from '../lib/auth/step_up.js';
import {
  resolveMyPayState,
  type MyPayLoadFailure,
  type MyPayState,
  type RealEmployerLink,
  type RealPayslipSnapshot,
} from './wagepro_store_my_pay.js';

export interface UseMyPayResult {
  state: MyPayState;
  /** Switches the payslip list to a different employer link (the multi-org picker, §6). */
  select_employer: (entity_id: string) => void;
  /** Re-runs both fetches from scratch. Only meaningful for the `error` state — a `step_up_required` state needs `restartSignIn()` (`lib/auth/step_up.ts`) instead, since a plain re-query can't upgrade an AAL1 token. */
  retry: () => void;
}

/** Classifies a caught `personalPayroll.*` error into the two `MyPayLoadFailure` buckets `resolveMyPayState` understands. */
function classifyFailure(err: unknown): MyPayLoadFailure {
  return isAal2Required(err) ? 'step_up' : 'error';
}

/** The signed-in employee's own payslip and pay history. */
export function useMyPay(): UseMyPayResult {
  const [links, setLinks] = React.useState<RealEmployerLink[] | undefined>(
    undefined,
  );
  const [linksFailure, setLinksFailure] = React.useState<
    MyPayLoadFailure | undefined
  >(undefined);
  const [selectedEntityId, setSelectedEntityId] = React.useState<string | null>(
    null,
  );
  const [snapshots, setSnapshots] = React.useState<
    RealPayslipSnapshot[] | undefined
  >(undefined);
  const [snapshotsFailure, setSnapshotsFailure] = React.useState<
    MyPayLoadFailure | undefined
  >(undefined);
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    setLinks(undefined);
    setLinksFailure(undefined);

    async function loadLinks(): Promise<void> {
      try {
        const result =
          await getTrpcClient().personalPayroll.listEmployerLinks.query();
        if (cancelled) return;
        setLinks(result.links);
        const defaultEntityId: string | null =
          result.links.at(0)?.entity_id ?? null;
        // Re-validate the CURRENT selection against the freshly-resolved
        // links, not just default it once. A `retry()` (or the links
        // resolver re-running for any reason) can observe that a
        // previously-selected employer link was revoked in the meantime --
        // keeping a stale `selected_entity_id` around would make the
        // payslips effect below fire for an entity_id the caller no longer
        // has an active link to, which the server correctly rejects with a
        // generic FORBIDDEN (classified as `'error'`, not
        // `no_employer_link`) that `retry()` would then loop on forever
        // (adversarial review finding, PR #22237). Falling back to the
        // first remaining link (or `null` if none remain) keeps the
        // resolver's state machine honest.
        setSelectedEntityId((current) =>
          current !== null &&
          result.links.some((link) => link.entity_id === current)
            ? current
            : defaultEntityId,
        );
      } catch (err) {
        if (cancelled) return;
        setLinksFailure(classifyFailure(err));
      }
    }

    void loadLinks();
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  React.useEffect(() => {
    if (selectedEntityId === null) return;
    // Captured as its own const so the nested async closure below references
    // a definitely-string value rather than relying on narrowing of
    // `selectedEntityId` (a useState value) surviving across the closure
    // boundary.
    const entityId: string = selectedEntityId;
    let cancelled = false;
    setSnapshots(undefined);
    setSnapshotsFailure(undefined);

    async function loadPayslips(): Promise<void> {
      try {
        const rows = await getTrpcClient().personalPayroll.listPayslips.query({
          entity_id: entityId,
        });
        if (cancelled) return;
        setSnapshots(rows);
      } catch (err) {
        if (cancelled) return;
        setSnapshotsFailure(classifyFailure(err));
      }
    }

    void loadPayslips();
    return () => {
      cancelled = true;
    };
  }, [selectedEntityId, reloadToken]);

  const state = resolveMyPayState({
    links,
    links_failure: linksFailure,
    selected_entity_id: selectedEntityId,
    snapshots,
    snapshots_failure: snapshotsFailure,
  });

  return {
    state,
    select_employer: (entity_id: string) => {
      // Clear the OLD employer's snapshots in the SAME event-handler batch
      // as the selection change, not just inside the payslips effect below.
      // `useEffect` runs as a passive effect after React commits and paints
      // -- without this, there is a real (if brief) committed render where
      // `selected_entity_id` already reads the NEW employer while
      // `snapshots` still holds the OLD employer's rows, which
      // `resolveMyPayState` would render as a `ready` state showing the new
      // employer's name over the previous employer's payslip data
      // (adversarial review finding, PR #22237). Clearing synchronously
      // here means the very next render already resolves to `loading`.
      setSelectedEntityId(entity_id);
      setSnapshots(undefined);
      setSnapshotsFailure(undefined);
    },
    retry: () => {
      setReloadToken((token) => token + 1);
    },
  };
}
